// @intent: Pure viewport/layout math for the native match-3 renderer.
// @flow: Match3LevelV1 + Match3SkinPackV1 -> layout rects -> Pixi sprite/input/probe placement.
// @stale: Keep coordinate semantics in design pixels; PixiRuntimeCanvas owns design->client transforms.

// @intent Design-pixel inset reserved for phone-frame notch/camera-cutout at top of viewport.
// @flow Applied as default safe.top in normalizeSafeArea so hud.y is always below the notch.
// @stale 2026-06-11 — 44px covers standard iOS notch (44pt) and most Android cutouts (~32–52px).
export const SAFE_AREA_TOP = 44;

export interface Match3Viewport {
  readonly width: number;
  readonly height: number;
}

export interface Match3SafeArea {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export interface Match3Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface Match3BoardRect extends Match3Rect {
  readonly cellSize: number;
  readonly cellGap?: number;
}

export interface Match3LayoutResult {
  readonly designWidth: number;
  readonly designHeight: number;
  readonly board: Match3BoardRect;
  readonly hud: Match3Rect;
  readonly tutorialHand: Match3Rect;
  readonly cta: Match3Rect;
  readonly endcard: Match3Rect;
}

export interface ComputeMatch3LayoutInput {
  readonly viewport: Match3Viewport;
  readonly rows: number;
  readonly cols: number;
  readonly safeArea?: Partial<Match3SafeArea>;
  readonly tileCellFill?: number;
  readonly boardSizing?: {
    readonly boardRect: Match3Rect;
    readonly tileSizePx: number;
    readonly tileGapPx: number;
  };
}

export interface Match3CellRect {
  readonly tileIndex: number;
  readonly row: number;
  readonly col: number;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly center: { readonly x: number; readonly y: number };
  readonly visual: Match3Rect;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function safePositiveInteger(value: number, fallback: number): number {
  return Number.isInteger(value) && value > 0 ? value : fallback;
}

function normalizeSafeArea(safeArea: Partial<Match3SafeArea> | undefined): Match3SafeArea {
  return {
    // Default top to SAFE_AREA_TOP so hud.y is always below the phone-frame notch when no
    // explicit safeArea is provided by callers (e.g. createMatch3RuntimeViewModel).
    top: Math.max(0, safeArea?.top ?? SAFE_AREA_TOP),
    right: Math.max(0, safeArea?.right ?? 0),
    bottom: Math.max(0, safeArea?.bottom ?? 0),
    left: Math.max(0, safeArea?.left ?? 0),
  };
}

function normalizeBoardSizing(
  sizing: ComputeMatch3LayoutInput['boardSizing'],
): Match3BoardRect | null {
  if (!sizing) return null;
  const rect = sizing.boardRect;
  if (
    !Number.isFinite(rect.x) ||
    !Number.isFinite(rect.y) ||
    !Number.isFinite(rect.width) ||
    !Number.isFinite(rect.height) ||
    rect.width <= 0 ||
    rect.height <= 0 ||
    !Number.isFinite(sizing.tileSizePx) ||
    sizing.tileSizePx <= 0
  ) {
    return null;
  }

  return {
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: rect.height,
    cellSize: Math.max(1, Math.round(sizing.tileSizePx)),
    cellGap: Math.max(0, Math.round(Number.isFinite(sizing.tileGapPx) ? sizing.tileGapPx : 0)),
  };
}

export function computeMatch3Layout(input: ComputeMatch3LayoutInput): Match3LayoutResult {
  const rows = safePositiveInteger(input.rows, 5);
  const cols = safePositiveInteger(input.cols, 5);
  const viewport = {
    width: Math.max(1, input.viewport.width),
    height: Math.max(1, input.viewport.height),
  };
  const safe = normalizeSafeArea(input.safeArea);
  const usableWidth = Math.max(1, viewport.width - safe.left - safe.right);
  const usableHeight = Math.max(1, viewport.height - safe.top - safe.bottom);
  const minSide = Math.min(viewport.width, viewport.height);
  const gap = Math.round(clamp(minSide * 0.025, 8, 18));
  const isLandscape = viewport.width > viewport.height;
  const hudHeight = Math.round(clamp(usableHeight * (isLandscape ? 0.13 : 0.095), 44, 86));
  const ctaHeight = Math.round(clamp(usableHeight * (isLandscape ? 0.13 : 0.095), 48, 88));
  const reservedVertical = hudHeight + ctaHeight + gap * 3;
  const maxBoardWidth = usableWidth * (isLandscape ? 0.5 : 0.9);
  const maxBoardHeight = Math.max(minSide * 0.32, usableHeight - reservedVertical);
  const computedCellSize = Math.floor(Math.max(12, Math.min(maxBoardWidth / cols, maxBoardHeight / rows)));
  const boardWidth = computedCellSize * cols;
  const boardHeight = computedCellSize * rows;
  const boardX = safe.left + Math.round((usableWidth - boardWidth) / 2);
  const boardY = safe.top + hudHeight + gap;
  const board = normalizeBoardSizing(input.boardSizing) ?? {
    x: boardX,
    y: boardY,
    width: boardWidth,
    height: boardHeight,
    cellSize: computedCellSize,
    cellGap: 0,
  };
  const hud: Match3Rect = {
    x: safe.left,
    y: safe.top,
    width: usableWidth,
    height: hudHeight,
  };
  const ctaWidth = Math.round(clamp(usableWidth * (isLandscape ? 0.42 : 0.72), 140, Math.min(usableWidth, 340)));
  const cta: Match3Rect = {
    x: safe.left + Math.round((usableWidth - ctaWidth) / 2),
    y: Math.min(viewport.height - safe.bottom - ctaHeight, board.y + board.height + gap),
    width: ctaWidth,
    height: ctaHeight,
  };
  const boardGap = Math.max(0, board.cellGap ?? 0);
  const boardStride = board.cellSize + boardGap;
  const gridWidth = board.cellSize * cols + boardGap * Math.max(0, cols - 1);
  const gridHeight = board.cellSize * rows + boardGap * Math.max(0, rows - 1);
  const gridStartX = board.x + (board.width - gridWidth) / 2;
  const gridStartY = board.y + (board.height - gridHeight) / 2;
  const handSize = Math.round(clamp(board.cellSize * 0.82, 28, 70));
  const handCenterX = gridStartX + boardStride * Math.min(cols - 1, Math.floor(cols / 2)) + board.cellSize / 2;
  const handCenterY = gridStartY + boardStride * Math.min(rows - 1, Math.floor(rows / 2)) + board.cellSize / 2;
  const tutorialHand: Match3Rect = {
    x: clamp(Math.round(handCenterX - handSize / 2), safe.left, viewport.width - safe.right - handSize),
    y: clamp(Math.round(handCenterY - handSize / 2), safe.top, viewport.height - safe.bottom - handSize),
    width: handSize,
    height: handSize,
  };
  const endcard: Match3Rect = {
    x: safe.left,
    y: safe.top,
    width: usableWidth,
    height: usableHeight,
  };

  return {
    designWidth: viewport.width,
    designHeight: viewport.height,
    board,
    hud,
    tutorialHand,
    cta,
    endcard,
  };
}

export function computeMatch3CellRects(
  board: Match3BoardRect,
  rows: number,
  cols: number,
  tileCellFill = 0.72,
): Match3CellRect[] {
  const safeRows = safePositiveInteger(rows, 5);
  const safeCols = safePositiveInteger(cols, 5);
  const fill = clamp(tileCellFill, 0.55, 0.98);
  const cellGap = Math.max(0, board.cellGap ?? 0);
  const maxCellWidth = Math.max(1, (board.width - cellGap * Math.max(0, safeCols - 1)) / safeCols);
  const maxCellHeight = Math.max(1, (board.height - cellGap * Math.max(0, safeRows - 1)) / safeRows);
  const cellSize = Math.min(board.cellSize, maxCellWidth, maxCellHeight);
  const cellStride = cellSize + cellGap;
  const gridWidth = cellSize * safeCols + cellGap * Math.max(0, safeCols - 1);
  const gridHeight = cellSize * safeRows + cellGap * Math.max(0, safeRows - 1);
  const startX = board.x + (board.width - gridWidth) / 2;
  const startY = board.y + (board.height - gridHeight) / 2;
  const visualSize = cellSize * fill;
  const cells: Match3CellRect[] = [];

  for (let row = 0; row < safeRows; row += 1) {
    for (let col = 0; col < safeCols; col += 1) {
      const tileIndex = row * safeCols + col;
      const x = startX + col * cellStride;
      const y = startY + row * cellStride;
      const center = { x: x + cellSize / 2, y: y + cellSize / 2 };
      cells.push({
        tileIndex,
        row,
        col,
        x,
        y,
        width: cellSize,
        height: cellSize,
        center,
        visual: {
          x: center.x - visualSize / 2,
          y: center.y - visualSize / 2,
          width: visualSize,
          height: visualSize,
        },
      });
    }
  }

  return cells;
}
